import { useCallback, useMemo } from 'react';
import { useCustom, useCustomMutation } from '@refinedev/core';

interface ProgressEntry {
  courseSlug: string;
  lessonSlug: string;
  completedAt: string;
}

/**
 * Hook pour gerer la progression d'apprentissage utilisateur.
 * Fetch via GET /api/v1/me/learning/progress, mutations via POST/DELETE.
 */
export function useLearningProgress() {
  const { data, isLoading, refetch } = useCustom<{ data: ProgressEntry[] }>({
    url: '/api/v1/me/learning/progress',
    method: 'get',
  });

  const { mutate } = useCustomMutation();

  const raw = data?.data;
  const entries: ProgressEntry[] = useMemo(() => {
    const d = (raw as Record<string, unknown>)?.data ?? raw;
    return Array.isArray(d) ? d : [];
  }, [raw]);

  const isLessonCompleted = useCallback(
    (courseSlug: string, lessonSlug: string): boolean =>
      entries.some((e) => e.courseSlug === courseSlug && e.lessonSlug === lessonSlug),
    [entries],
  );

  const completedCountForCourse = useCallback(
    (courseSlug: string): number => entries.filter((e) => e.courseSlug === courseSlug).length,
    [entries],
  );

  const completeLesson = useCallback(
    (courseSlug: string, lessonSlug: string) => {
      mutate(
        { url: `/api/v1/me/learning/${courseSlug}/${lessonSlug}/complete`, method: 'post', values: {} },
        { onSuccess: () => refetch() },
      );
    },
    [mutate, refetch],
  );

  const uncompleteLesson = useCallback(
    (courseSlug: string, lessonSlug: string) => {
      mutate(
        { url: `/api/v1/me/learning/${courseSlug}/${lessonSlug}/complete`, method: 'delete', values: {} },
        { onSuccess: () => refetch() },
      );
    },
    [mutate, refetch],
  );

  return {
    entries,
    isLoading,
    isLessonCompleted,
    completedCountForCourse,
    completeLesson,
    uncompleteLesson,
    totalCompleted: entries.length,
  };
}
